import * as React from 'react';
import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { GlobalUseStyles } from './GlobalUseStyles';
import clsx from 'clsx';
import { PDMLogin } from './RestService';
import { useSnackbar } from 'react-simple-snackbar';
import { errorSnackbarOptions, successSnackbarOptions, warningSnackbarOptions } from './SnackbarOptions';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import config from '../config';
import CryptoJS from 'crypto-js';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: '400px',
    padding: theme.spacing(2),
  },
  closeButton: {
    marginLeft: theme.spacing(1),
  },
}));

export default function ModalPopup({
  open,
  onClose,
  title,
  instance,
  accounts,
  setPdmUsername,
  initialUsername = '',
  initialPassword = '',
  onResolve,
}) {
  const classes = useStyles();
  const globalClasses = GlobalUseStyles();
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState(initialPassword);
  const [openSuccessSnackbar, closeSuccessSnackbar] = useSnackbar(successSnackbarOptions());
  const [openErrorSnackbar, closeErrorSnackbar] = useSnackbar(errorSnackbarOptions());
  const [openWarningSnackbar, closeWarningSnackbar] = useSnackbar(warningSnackbarOptions());
  const [isInvalid, setIsInvalid] = useState(false);
  
  const generateRandomString = (input) => {
    const maxLength = 16;
    const predefinedChars = 'KONECWSWEBAPPUSR';
    let result = input.slice(0, maxLength);
    let i = 0;
    while (result.length < maxLength) {
      result += predefinedChars[i % predefinedChars.length];
      i++;
    }
    return result;
  };

  useEffect(() => {
    console.log("MODAL: USEEFFECT");
    if (open) {
      setUsername(initialUsername);
      setPassword(initialPassword);
      setIsInvalid(false);
    }
  }, [open, initialUsername, initialPassword]);
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = { username: username.toLowerCase(), password };
    sessionStorage.setItem('pdmUsername', formData.username);
	let sKey = generateRandomString(formData.username.trim().toUpperCase()); 
    const encryptedPasswordData = encryptPassword(formData.password, sKey);
    sessionStorage.setItem('pdmPassword', encryptedPasswordData);

    console.log('PDM Login initiated');
    openSuccessSnackbar(
      <div style={{ whiteSpace: 'nowrap' }}>
        <ErrorIcon
          style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '14px' }}
          className={globalClasses.whiteIcon}
        />
        <p style={{ display: 'inline-block', verticalAlign: 'middle', whiteSpace: 'pre-wrap' }}>
          {'Authenticating...'}
        </p>
      </div>,
      300000
    );

    let loginReply = await PDMLogin(instance);
    console.log(loginReply);
    if (loginReply === formData.username || loginReply === accounts[0]?.username) {
      setPdmUsername(formData.username);
      console.log('Login was successful');
      sessionStorage.setItem('pdmLoginFailed', 'false');
      closeSuccessSnackbar();
      onResolve({ success: true, username: formData.username });
      onClose(); // Close modal on success
    } else {
      sessionStorage.setItem('pdmUsername', '');
      sessionStorage.setItem('pdmPassword', '');
      let errorMessage = loginReply || 'Login failed: PDM unreachable.';
      console.log('Login failed:', errorMessage);
      sessionStorage.setItem('pdmLoginFailed', 'true');
      closeSuccessSnackbar();
      openWarningSnackbar(
        <div style={{ whiteSpace: 'nowrap' }}>
          <ErrorIcon
            style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '14px' }}
            className={globalClasses.whiteIcon}
          />
          <p style={{ display: 'inline-block', verticalAlign: 'middle', whiteSpace: 'pre-wrap' }}>
            {errorMessage}
          </p>
        </div>,
        300000
      );
      if (errorMessage.includes('Invalid Username/Password')) {
        setIsInvalid(true);
        setUsername(formData.username); // Preserve username
        setPassword(''); // Clear password
        // Modal stays open (no need to set open state since it's controlled by parent)
      } else {
        onResolve({ success: false, error: errorMessage });
        onClose(); // Close modal for other errors
      }
    }
  };

  const handleCancel = () => {
    setUsername('');
    setPassword('');
    setIsInvalid(false);
    onResolve({ success: false, canceled: true });
    onClose();
  };

  const encryptPassword = (password, secretKey) => {
    let skey = CryptoJS.enc.Utf8.parse(secretKey);
    let encryptedBytes = CryptoJS.AES.encrypt(password, skey, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    let encryptedString = encryptedBytes.toString();
    return encryptedString;
  };

  return (
    <Dialog
      open={open || isInvalid}
      onClose={onClose}
      aria-labelledby="modal-title"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="modal-title">
        <Typography variant="h5">{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{ style: { textTransform: 'lowercase' } }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <DialogActions>
            <Button
              onClick={handleCancel}
              className={globalClasses.blueButton}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={clsx(globalClasses.blueButton, classes.closeButton)}
            >
              Sign In
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}